import { useState } from 'react'
import { IconArrowLeft, IconMailCheck, IconSend, IconUser } from '@tabler/icons-react'
import { Link } from 'react-router-dom'

import { authApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { AuthFormBrand, AuthShell, StepsAside, type StepState } from '@/components/auth/AuthShell'
import { tr } from '@/i18n/tr'
import { Alert, Button, TextField, useForm } from '@/ui'

/**
 * Parolamı unuttum.
 *
 * <p>Sunucu, hesap olsun olmasın AYNI yanıtı verir; bu ekran da "bulunamadı" demez.
 * Aksi halde giriş yapmamış biri hangi kullanıcı adlarının ya da e-postaların kayıtlı
 * olduğunu buradan öğrenebilirdi.
 */
export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const form = useForm({
    initialValues: { identifier: '' },
    validate: { identifier: (v) => (v.trim() ? null : tr.common.required) },
  })

  const submit = form.onSubmit(async (values) => {
    setBusy(true)
    setError(null)
    try {
      await authApi.forgotPassword(values.identifier.trim())
      setSent(true)
    } catch (err) {
      setError(errorMessage(err, 'İstek gönderilemedi. Biraz sonra tekrar deneyin.'))
    } finally {
      setBusy(false)
    }
  })

  const steps: { label: string; hint: string; state: StepState }[] = [
    { label: 'Hesabınızı belirtin', hint: 'Kullanıcı adınız ya da kayıtlı e-posta adresiniz.', state: sent ? 'done' : 'now' },
    { label: 'E-postanızı kontrol edin', hint: 'Bağlantı 1 saat geçerlidir ve bir kez kullanılır.', state: sent ? 'now' : 'todo' },
    { label: 'Yeni parolanızı belirleyin', hint: 'Ardından yeni parolanızla giriş yapın.', state: 'todo' },
  ]

  return (
    <AuthShell asideLabel="Adımlar" aside={<StepsAside steps={steps} />}>
      <form className="nx-auth-form nx-auth-form--wide" onSubmit={submit} noValidate>
        <AuthFormBrand />
        <Link to="/login" className="nx-auth-back ui-focusable">
          <IconArrowLeft size={16} stroke={1.8} aria-hidden="true" />{tr.auth.backToLogin}
        </Link>
        <div className="nx-auth-form__head">
          <h1>{tr.auth.forgotPassword}</h1>
          <p>Size parolanızı yeniden belirleyeceğiniz bir bağlantı gönderelim.</p>
        </div>

        {sent ? (
          <Alert tone="success" title="E-postanızı kontrol edin"
            icon={<IconMailCheck size={18} stroke={1.8} aria-hidden="true" />}>
            Bu bilgilerle eşleşen bir hesap varsa parola sıfırlama bağlantısı kayıtlı e-posta adresine gönderildi.
            Birkaç dakika içinde gelmezse gereksiz (spam) klasörüne bakın ya da yöneticinizle iletişime geçin.
          </Alert>
        ) : (
          <>
            {error && <Alert tone="danger">{error}</Alert>}
            <TextField label="Kullanıcı adı ya da e-posta" placeholder="kullanici.adi" size="lg" autoFocus
              autoComplete="username" autoCapitalize="none" spellCheck={false}
              leftSection={<IconUser size={18} stroke={1.8} aria-hidden="true" />}
              {...form.getInputProps('identifier')} />
            <Button type="submit" size="lg" full loading={busy} className="nx-auth-submit"
              leftIcon={<IconSend size={18} stroke={1.8} aria-hidden="true" />}>
              Bağlantı gönder
            </Button>
          </>
        )}

        <p className="nx-auth-note">Hesabınızda e-posta adresi yoksa parolanızı sistem yöneticiniz sıfırlayabilir.</p>
      </form>
    </AuthShell>
  )
}
